// Filter-context configuration for THIS dashboard. All project SQL for the two
// filter contexts lives here; `utils/createFilterContext.js` holds none.
//
// Each SELECT below is copied verbatim from the classes this replaced
// (`FilterItemsContext` / `FilterStatusHistoryContext`) so the parity harness
// in `tooling/parity/filter-context/` compares like for like.

/**
 * The 18 aliased item columns, shared by `filteredItems` (items context) and
 * `filteredItemsByStatusHistory` (history context) — identical in both classes.
 */
const ITEM_COLUMNS = `
      SELECT
          i.ClientId AS clientId,
          i.Id AS id,
          i.LocationId AS locationId,
          i.SubItemId AS subItemId,
          i.StatusId AS statusId,
          i.StatusCreatedDate AS statusCreatedDate,
          i.AssigneeTeamId AS assigneeTeamId,
          i.AssigneeUserIds AS assigneeUserIds,
          i.AssigneeCreatedDate AS assigneeCreatedDate,
          i.SubItemTemplateId AS subItemTemplateId,
          i.ControlledByTeamId AS controlledByTeamId,
          i.CreatedByTeamId AS createdByTeamId,
          i.CreatedByUserId AS createdByUserId,
          i.ClosedDate AS closedDate,
          i.CreatedDate AS createdDate,
          i.ItemTemplateId AS itemTemplateId,
          i.ProfileId AS profileId,
          i."Type" AS type
      FROM items i`;

/**
 * Items filter context — the app-wide one, initialised at boot by App.vue.
 * Single VARCHAR key, unique index, and a `showOpenItemsOnly` base restriction
 * that must be re-intersected (filter views read the raw `items` table).
 */
export const itemsFilterConfig = {
  id: 'items',
  sourceTable: 'items',
  contextTable: 'filter_context',
  keyColumns: ['SubItemId'],
  uniqueIndex: true,

  // Only include items whose status is NOT terminal (open items only).
  baseSelect: ({ showOpenItemsOnly = false } = {}) =>
    showOpenItemsOnly
      ? `
        SELECT DISTINCT i.SubItemId
        FROM items i
        JOIN status_templates st ON i.StatusId = st.Id
        WHERE st.IsTerminal = FALSE
      `
      : null,

  views: [
    {
      name: 'filteredItems',
      select: ({ contextTable }) => `${ITEM_COLUMNS}
      INNER JOIN ${contextTable} fc USING(SubItemId)`,
    },
  ],
};

/**
 * Status-history filter context — built lazily by ActivityPage after it derives
 * `status_history_temporal`. Composite key; no base restriction.
 */
export const statusHistoryFilterConfig = {
  id: 'statusHistory',
  sourceTable: 'status_history_temporal',
  contextTable: 'status_history_filter_context',
  keyColumns: [
    { name: 'subItemId', type: 'VARCHAR' },
    { name: 'validFrom', type: 'TIMESTAMP' },
    { name: 'statusId', type: 'VARCHAR' },
  ],

  views: [
    {
      name: 'filteredStatusHistory',
      select: ({ contextTable }) => `
      SELECT
          sht.statusId AS statusId,
          sht.locationId,
          sht.profileId,
          sht.subItemTemplateId,
          sht.createdByTeamId AS createdByTeamId,
          sht.createdByUserId AS createdByUserId,
          sht.subItemId AS subItemId,
          sht.validFrom AS validFrom,
          sht.validTo AS validTo
      FROM status_history_temporal sht
      INNER JOIN ${contextTable} fc ON
          sht.subItemId = fc.subItemId AND
          sht.validFrom = fc.validFrom AND
          sht.statusId = fc.statusId`,
    },
    {
      // Items view for the Activity page's ItemsGrid. This must NOT be named
      // filteredItems: that view belongs to the items context, and replacing it
      // here would permanently detach every other page's charts and grids from
      // their filter_context until the app reloads.
      name: 'filteredItemsByStatusHistory',
      select: ({ contextTable }) => `${ITEM_COLUMNS}
      WHERE EXISTS (
        SELECT 1 FROM ${contextTable} fc
        WHERE fc.subItemId = i.subItemId
      )`,
    },
  ],
};
